import { useEffect } from "react";

/*
 * Halloween 2026 opening animation (public/seasonal/halloween-intro.js) for selected pages.
 * Plays between 27 Oct and 2 Nov 2026, at most once every 6 hours per page in a browser tab
 * (`page` gives each page its own timestamp), so login, dashboard and POS menu each play.
 * The script's own year-less date check and single "seen" flag are bypassed with
 * play({ force: true }); the checks below replace them.
 */
const START = new Date(2026, 9, 27); // 27 Oct 2026, 00:00 local time
const END = new Date(2026, 10, 2, 23, 59, 59, 999); // 2 Nov 2026, end of day
const REPLAY_AFTER_MS = 6 * 60 * 60 * 1000; // same tab: play again once 6 hours have passed
const SCRIPT_ID = "halloween-intro-script";
const OVERLAY_SELECTOR = ".hwi"; // root element the script adds while it plays

const loadScript = () =>
  new Promise((resolve, reject) => {
    if (window.HalloweenIntro) {
      resolve(window.HalloweenIntro);
      return;
    }
    let script = document.getElementById(SCRIPT_ID);
    if (!script) {
      script = document.createElement("script");
      script.id = SCRIPT_ID;
      script.src = `${process.env.PUBLIC_URL}/seasonal/halloween-intro.js`;
      script.dataset.manual = ""; // no autoplay; this component decides when to play
      document.body.appendChild(script);
    }
    script.addEventListener("load", () => resolve(window.HalloweenIntro), { once: true });
    script.addEventListener("error", reject, { once: true });
  });

const HalloweenIntro = ({ page }) => {
  useEffect(() => {
    const now = new Date();
    if (now < START || now > END) return undefined;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return undefined;

    // value = time (ms) this page last played its intro in this tab
    const key = `halloween-intro-seen-${page}`;
    try {
      const lastPlayed = Number(sessionStorage.getItem(key)) || 0;
      if (Date.now() - lastPlayed < REPLAY_AFTER_MS) return undefined;
    } catch (err) {
      // storage blocked (private mode etc.): just play
    }

    let cancelled = false;
    let retryTimer;
    const start = (intro) => {
      if (cancelled || !intro) return;
      // another page's intro still on screen (e.g. a fast login): wait for it to finish
      if (document.querySelector(OVERLAY_SELECTOR)) {
        retryTimer = setTimeout(() => start(intro), 500);
        return;
      }
      try {
        sessionStorage.setItem(key, String(Date.now())); // stamp only when it really plays
      } catch (err) {
        // ignore
      }
      intro.play({ force: true });
    };

    loadScript().then(start).catch(() => {});
    return () => {
      cancelled = true;
      clearTimeout(retryTimer);
    };
  }, [page]);

  return null;
};

export default HalloweenIntro;
